import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Tab, Nav, Alert, Spinner, Form } from 'react-bootstrap';
import { Save, Eye, FileText, InfoCircle, Shield, ShieldLock, Trash } from 'react-bootstrap-icons';
import {
  Box,
  Breadcrumbs,
  Link,
  Typography,
  Paper
} from "@mui/material";
import API_BASE_URL from '../../../config/api';
import { getAdminAuthHeader } from '../../../services/auth';

/* ── Shared style tokens (matching Users page) ── */
const primaryGradient = "linear-gradient(135deg, #1a237e, #0d47a1)";
const fieldStyle = {
  borderRadius: "8px",
  border: "1.5px solid #dde4f5",
  fontSize: "0.875rem",
};
const labelStyle = { fontWeight: 600, fontSize: "0.8rem", color: "#1a237e" };

const getApiErrorMessage = (result, fallback) => {
  if (!result) {
    return fallback;
  }

  if (typeof result === 'string') {
    return result;
  }

  if (Array.isArray(result.errors) && result.errors.length > 0) {
    return result.errors
      .map(error => error.message || error.msg || error)
      .filter(Boolean)
      .join(', ');
  }

  return result.message || result.error || result.msg || fallback;
};

const parseApiResponse = async (response) => {
  const text = await response.text();
  let result = null;

  if (text) {
    try {
      result = JSON.parse(text);
    } catch (error) {
      result = text;
    }
  }

  if (!response.ok) {
    const message = getApiErrorMessage(result, `HTTP error! status: ${response.status}`);
    throw new Error(message);
  }

  return result;
};

const StaticContent = () => {
  const [activeTab, setActiveTab] = useState('aboutUs');
  const [content, setContent] = useState({
    aboutUs: '',
    termsAndConditions: '',
    privacyPolicy: '',
    deleteAccount: ''
  });
  const [originalContent, setOriginalContent] = useState({
    aboutUs: '',
    termsAndConditions: '',
    privacyPolicy: '',
    deleteAccount: ''
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [previewContent, setPreviewContent] = useState({ show: false, title: '', content: '' });

  useEffect(() => {
    fetchContent(activeTab);
  }, [activeTab]);

  const fetchContent = async (section) => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    
    try {
      console.log('Fetching content for section:', section);
      
      const response = await fetch(`${API_BASE_URL}/api/static-content/get/${section}`, {
        method: 'GET',
        headers: {
          ...getAdminAuthHeader(),
          'Content-Type': 'application/json',
        },
      });

      console.log('Response status:', response.status);

      const result = await parseApiResponse(response);
      console.log('API Response:', result);

      if (result.data) {
        const contentValue = result.data.content || '';
        
        setContent(prev => ({
          ...prev,
          [section]: contentValue
        }));
        setOriginalContent(prev => ({
          ...prev,
          [section]: contentValue
        }));
        
        if (contentValue) {
          setMessage({ type: 'success', text: `${getSectionTitle(section)} content loaded successfully` });
        } else {
          setMessage({ type: 'info', text: `No content found for ${getSectionTitle(section)}. You can create new content.` });
        }
      } else {
        setContent(prev => ({
          ...prev,
          [section]: ''
        }));
        setOriginalContent(prev => ({
          ...prev,
          [section]: ''
        }));
        
        setMessage({ type: 'info', text: `No existing content found for ${getSectionTitle(section)}. You can create new content.` });
      }
    } catch (error) {
      console.error('Error fetching content:', error);
      let errorMessage = 'Error fetching content';
      
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        errorMessage = 'Network error: Cannot connect to server. Please check your connection and API URL.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setMessage({ type: 'danger', text: errorMessage });
      
      setContent(prev => ({
        ...prev,
        [section]: ''
      }));
      setOriginalContent(prev => ({
        ...prev,
        [section]: ''
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!content[activeTab].trim()) {
      setMessage({ type: 'warning', text: 'Content cannot be empty' });
      return;
    }

    setSaving(true);
    setMessage({ type: '', text: '' });
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/static-content/add`, {
        method: 'POST',
        headers: {
          ...getAdminAuthHeader(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          section: activeTab,
          content: content[activeTab]
        }),
      });

      console.log('Save response status:', response.status);

      const result = await parseApiResponse(response);
      console.log('Save response:', result);

      if (result.success) {
        setMessage({ type: 'success', text: `${getSectionTitle(activeTab)} saved successfully!` });
        setOriginalContent(prev => ({
          ...prev,
          [activeTab]: content[activeTab]
        }));
      } else {
        setMessage({ type: 'danger', text: result.message || 'Failed to save content' });
      }
    } catch (error) {
      console.error('Error saving content:', error);
      let errorMessage = 'Error saving content';
      
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        errorMessage = 'Network error: Cannot connect to server. Please check your connection.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setMessage({ type: 'danger', text: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    const titles = {
      aboutUs: 'About Us',
      termsAndConditions: 'Terms and Conditions',
      privacyPolicy: 'Privacy Policy',
      deleteAccount: 'Delete Account'
    };

    setPreviewContent({
      show: true,
      title: titles[activeTab],
      content: content[activeTab]
    });
  };

  const hasChanges = () => {
    return content[activeTab] !== originalContent[activeTab];
  };

  const getSectionTitle = (section) => {
    const titles = {
      aboutUs: 'About Us',
      termsAndConditions: 'Terms & Conditions',
      privacyPolicy: 'Privacy Policy',
      deleteAccount: 'Delete Account'
    };
    return titles[section];
  };

  const getSectionIcon = (section) => {
    switch(section) {
      case 'aboutUs':
        return <InfoCircle size={16} />;
      case 'termsAndConditions':
        return <Shield size={16} />;
      case 'privacyPolicy':
        return <ShieldLock size={16} />;
      case 'deleteAccount':
        return <Trash size={16} />;
      default:
        return <FileText size={16} />;
    }
  };

  // Simple textarea version that works without external dependencies
  const renderEditor = (section) => {
    return (
      <Form.Control
        as="textarea"
        rows={15}
        value={content[section]}
        onChange={(e) => setContent(prev => ({ ...prev, [section]: e.target.value }))}
        placeholder={`Enter ${getSectionTitle(section)} content here...`}
        style={{ 
          ...fieldStyle,
          fontFamily: 'Arial, sans-serif',
          fontSize: '14px',
          lineHeight: '1.5',
          padding: '12px',
          resize: 'vertical'
        }}
      />
    );
  };

  return (
    <Box sx={{ p: 3.5, background: "#f0f4ff", minHeight: "100vh" }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 2.5, fontSize: "0.8rem" }}>
        <Link underline="hover" color="inherit" href="/dashboard">
          Dashboard
        </Link>
        <Typography
          color="primary"
          sx={{ fontSize: "0.8rem", fontWeight: 600 }}
        >
          Static Content Management
        </Typography>
      </Breadcrumbs>

      {/* Page header */}
      <Box
        display="flex"
        alignItems="center"
        mb={3}
        gap={1.5}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "11px",
            background: primaryGradient,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 12px rgba(13,71,161,0.3)",
          }}
        >
          <FileText size={20} color="#fff" />
        </Box>
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 800, color: "#1a237e", lineHeight: 1.2 }}
          >
            Static Content Management
          </Typography>
          <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
            Manage website content sections
          </Typography>
        </Box>
      </Box>

      <Container fluid>
        <Row>
          <Col>
            <Paper
              sx={{
                background: "#fff",
                borderRadius: "14px",
                border: "1px solid #e4eaf8",
                overflow: "hidden",
                boxShadow: "0 2px 12px rgba(13,71,161,0.06)",
              }}
            >
              <Box sx={{ p: 2.5 }}>
                {message.text && (
                  <Alert 
                    variant={message.type} 
                    onClose={() => setMessage({ type: '', text: '' })} 
                    dismissible
                    style={{ borderRadius: '8px', marginBottom: '20px' }}
                  >
                    {message.text}
                  </Alert>
                )}

                <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
                  <Row>
                    <Col md={3}>
                      <Nav variant="pills" className="flex-column">
                        {['aboutUs', 'termsAndConditions', 'privacyPolicy', 'deleteAccount'].map(section => (
                          <Nav.Item key={section} className="mb-2">
                            <Nav.Link 
                              eventKey={section} 
                              className="d-flex align-items-center"
                              style={{
                                borderRadius: '8px',
                                color: activeTab === section ? '#fff' : '#1a237e',
                                backgroundColor: activeTab === section ? '#0d47a1' : 'transparent',
                                border: '1px solid #e4eaf8',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <Box component="span" sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
                                {getSectionIcon(section)}
                              </Box>
                              {getSectionTitle(section)}
                            </Nav.Link>
                          </Nav.Item>
                        ))}
                      </Nav>
                    </Col>
                    
                    <Col md={9}>
                      <Tab.Content>
                        {['aboutUs', 'termsAndConditions', 'privacyPolicy', 'deleteAccount'].map(section => (
                          <Tab.Pane key={section} eventKey={section}>
                            <Paper
                              sx={{
                                border: '1px solid #e4eaf8',
                                borderRadius: '12px',
                                overflow: 'hidden'
                              }}
                            >
                              <Box
                                sx={{
                                  p: 2,
                                  background: primaryGradient,
                                  color: '#fff',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center'
                                }}
                              >
                                <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {getSectionIcon(section)}
                                  Edit {getSectionTitle(section)}
                                </Typography>
                                <Box display="flex" alignItems="center" gap={1}>
                                  {hasChanges() && (
                                    <span
                                      className="badge px-3 py-2 rounded-pill"
                                      style={{
                                        background: '#ffb74d',
                                        color: '#1a237e',
                                        fontSize: '0.7rem'
                                      }}
                                    >
                                      Unsaved Changes
                                    </span>
                                  )}
                                  <Button
                                    variant="outline-light"
                                    size="sm"
                                    className="me-2 d-flex align-items-center gap-1"
                                    onClick={handlePreview}
                                    disabled={loading || !content[activeTab]}
                                    style={{ borderRadius: '6px' }}
                                  >
                                    <Eye size={14} />
                                    Preview
                                  </Button>
                                  <Button
                                    variant="light"
                                    size="sm"
                                    onClick={handleSave}
                                    disabled={loading || saving || !hasChanges()}
                                    className="d-flex align-items-center gap-1"
                                    style={{ borderRadius: '6px', background: '#fff', color: '#0d47a1' }}
                                  >
                                    {saving ? (
                                      <>
                                        <Spinner animation="border" size="sm" className="me-1" />
                                        Saving...
                                      </>
                                    ) : (
                                      <>
                                        <Save size={14} />
                                        Save Changes
                                      </>
                                    )}
                                  </Button>
                                </Box>
                              </Box>
                              <Box sx={{ p: 2.5 }}>
                                {loading ? (
                                  <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    flexDirection="column"
                                    gap={2}
                                    py={5}
                                  >
                                    <Spinner
                                      animation="border"
                                      style={{
                                        color: "#0d47a1",
                                        width: 36,
                                        height: 36,
                                        borderWidth: 3,
                                      }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                      Loading content...
                                    </Typography>
                                  </Box>
                                ) : (
                                  renderEditor(section)
                                )}
                              </Box>
                            </Paper>
                          </Tab.Pane>
                        ))}
                      </Tab.Content>
                    </Col>
                  </Row>
                </Tab.Container>
              </Box>
            </Paper>
          </Col>
        </Row>
      </Container>

      {/* Preview Modal */}
      {previewContent.show && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1050,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2
          }}
        >
          <Paper
            sx={{
              width: '100%',
              maxWidth: '800px',
              maxHeight: '90vh',
              borderRadius: '14px',
              overflow: 'hidden',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
            }}
          >
            <Box
              sx={{
                background: primaryGradient,
                color: '#fff',
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Eye size={18} />
                Preview: {previewContent.title}
              </Typography>
              <Button
                variant="link"
                onClick={() => setPreviewContent({ show: false, title: '', content: '' })}
                style={{ color: '#fff', padding: 0, border: 'none' }}
              >
                <span style={{ fontSize: '24px', lineHeight: 1 }}>&times;</span>
              </Button>
            </Box>
            <Box sx={{ p: 3, maxHeight: '60vh', overflowY: 'auto' }}>
              <Box
                sx={{
                  p: 2,
                  border: '1px solid #e4eaf8',
                  borderRadius: '8px',
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'Arial, sans-serif',
                  lineHeight: 1.6,
                  fontSize: '0.9rem',
                  color: '#1a237e'
                }}
              >
                {previewContent.content}
              </Box>
            </Box>
            <Box sx={{ p: 2, borderTop: '1px solid #e4eaf8', display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outline-secondary"
                onClick={() => setPreviewContent({ show: false, title: '', content: '' })}
                style={{ borderRadius: '8px' }}
              >
                Close
              </Button>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default StaticContent;
